import { useEffect } from "react";
import {
  isToolAllowed,
  useReviewEditorStore,
  type EditorSurface,
  type EditorToolKind,
} from "@/stores/review-editor";

/**
 * Coach Review keyboard shortcuts.
 *
 * Body-attribute scoping: shortcuts fire only when
 * `<body data-coach-review-source>` is set (which the SourceToggle
 * sets on mount + clears on unmount). That keeps the M4 keys from
 * firing on /canvas-demo or any other route that mounts a canvas
 * for its own purposes.
 *
 * Input-focus skip-guard: shortcuts skip when focus is inside
 * `<input>`, `<textarea>`, `<select>`, or `[contenteditable]`. Without
 * this, typing the letter "A" into the composer's title field
 * would arm the arrow tool.
 *
 * Modifier-key skip: any of Cmd / Ctrl / Alt held → skip. This
 * leaves Cmd+S (browser save), Cmd+F (browser find), etc.
 * untouched. Shift is NOT skipped (we use Shift on its own as a
 * "secondary" modifier in legacy code; M4 doesn't need it).
 *
 * Shortcuts (mirror the legacy review.js mapping + the M3 canvas
 * shortcuts):
 *   V → select
 *   A → arrow
 *   L → line
 *   Z → zone
 *   T → label
 *   F → freehand (pen)
 *   P → player          (tactical-only; falls back to select on
 *                        telestrator surface — armTool's surface-
 *                        validity gate handles this silently)
 *   B → ball            (tactical-only; same fallback)
 *   S → spotlight       (telestrator-only)
 *   D → dim             (telestrator-only)
 *   G → formation       (telestrator-only)
 *   Escape → disarm (back to select) + clear selection
 *   ?  → toggle the help popover (parent owns the state)
 *
 * Note on Save shortcut: the legacy code had `S` mapped to "save
 * note." That conflicts with the M3 telestrator's `S` = spotlight.
 * For M4 we keep S → spotlight (consistent with M3 + the on-
 * canvas tool palette) and route save via the submit button only.
 * If a future review brings back save-on-S, replace via a
 * "preferences" prop here rather than hardcoding the rebind.
 */

// Tool-arming letter bindings.
//
// V / B / L / F were rebound away from tools (select / ball / line /
// freehand) because the Coach Review v2 keyboard table claims them
// for review actions — V/B switch source, L toggles loop, F toggles
// focus mode. Those four tools are still reachable via the `1`–`4`
// quick-pick (the first four tools of the active surface) and by
// clicking the tool grid. The remaining letters keep their tool
// meaning. Mirrors the standalone reference, where the tool palette
// shows numeric hotkeys (`1`–`4`) and the letter keys drive the
// review-action layer.
const TOOL_BINDINGS: Record<string, EditorToolKind> = {
  A: "arrow",
  Z: "zone",
  T: "label",
  P: "player",
  S: "spotlight",
  D: "dim",
  G: "formation",
};

export type ReviewKeyboardShortcutsOptions = {
  enabled?:boolean;
  /** When set, pressing `?` calls this. The parent owns the help
   * popover state. */
  onToggleHelp?: () => void;
  // ── Coach Review v2 review-action keys ──────────────────────────
  // These are the clip-/note-flow shortcuts the v2 layout adds. They
  // are kept here (not in `useVideoKeyboardShortcuts`) because they
  // drive Coach Review's composer / clip-window state, not the
  // `<video>` element. Each is optional — the parent wires only the
  // ones meaningful for the current mode; an unwired key is a no-op.
  //
  // The full Coach Review v2 review-action key set (the standalone
  // reference's keyboard table):
  //   C → enter clip mode        M → mark moment (open note composer)
  //   I → set clip IN            O → set clip OUT
  //   , → step back 1 frame      . → step forward 1 frame
  //   Shift+, / Shift+. → step back / forward 1 second
  //   L → toggle loop            F → toggle focus mode
  //   V → switch to Video        B → switch to Board
  //   1–4 → quick-pick the active surface's first four tools
  // V / B / L / F were rebound off the tool letters for this — see
  // the TOOL_BINDINGS comment above. Each callback is optional; an
  // unwired key is a no-op.
  /** `C` — enter clip mode. */
  onEnterClip?: () => void;
  /** `M` — mark moment (open the note composer). */
  onMarkMoment?: () => void;
  /** `I` — set the clip IN point at the playhead. */
  onSetClipIn?: () => void;
  /** `O` — set the clip OUT point at the playhead. */
  onSetClipOut?: () => void;
  /** `[` — park the playhead on the clip's first frame (where the
   * clip's freeze-frame telestration lives). */
  onGoToClipIn?: () => void;
  /** `,` / `.` — step the playhead by one frame (`shift` ⇒ 1s). */
  onFrameStep?: (direction: -1 | 1, shift: boolean) => void;
  /** `L` — toggle loop-within-clip-range playback. */
  onToggleLoop?: () => void;
  /** `F` — toggle focus mode. */
  onToggleFocus?: () => void;
  /** `V` / `B` — switch the active source surface. */
  onSwitchSource?: (next: "video" | "tactical_board") => void;
};

/** The four tools `1`–`4` quick-pick, per surface. Both surfaces
 * share the same first four (select / arrow / line / zone) — listed
 * per-surface anyway so the mapping survives a future divergence.
 * `armTool` re-validates against the surface, so an out-of-set tool
 * would fall back to select; keeping the lists explicit avoids that
 * silent fallback. */
const QUICK_TOOLS: Record<EditorSurface, ReadonlyArray<EditorToolKind>> = {
  telestrator: ["select", "arrow", "line", "zone"],
  tactical: ["select", "arrow", "line", "zone"],
};

export function useReviewKeyboardShortcuts({
  enabled=true,
  onToggleHelp,
  onEnterClip,
  onMarkMoment,
  onSetClipIn,
  onSetClipOut,
  onGoToClipIn,
  onFrameStep,
  onToggleLoop,
  onToggleFocus,
  onSwitchSource,
}: ReviewKeyboardShortcutsOptions = {}) {
  const armTool = useReviewEditorStore((s) => s.armTool);
  const disarm = useReviewEditorStore((s) => s.disarm);
  const surface = useReviewEditorStore((s) => s.surface);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!enabled) return;
      // Body-attribute scope: skip if Coach Review isn't the
      // active surface. The SourceToggle sets + clears this.
      if (!document.body.dataset.coachReviewSource) return;

      // Modifier-key skip (browser shortcuts win).
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // Input-focus skip-guard.
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          target.isContentEditable
        ) {
          return;
        }
      }

      const isHelpKey = e.key === "?" || (e.key === "/" && e.shiftKey);
      if (document.querySelector('[role="alertdialog"],[role="listbox"]')) return;
      const activeModals = document.querySelectorAll('[data-coach-modal-active="true"]');
      // Help owns its toggle while open. Every other editor shortcut stays
      // suspended, including when another modal takes ownership above help.
      if (activeModals.length && !(
        isHelpKey && activeModals.length === 1 &&
        activeModals[0].querySelector('[data-coach-review-help]')
      )) return;

      // Help popover: ? triggers regardless of surface.
      if (isHelpKey) {
        e.preventDefault();
        onToggleHelp?.();
        return;
      }

      // Esc → disarm + clear selection (per-surface, via the
      // review-editor store).
      if (e.key === "Escape") {
        // Don't preventDefault — Esc may close other modals too.
        disarm();
        return;
      }

      // ── v2 review-action keys ──────────────────────────────────
      // Frame step — `,` / `.` (Shift ⇒ 1s). Handled before the
      // tool letters because these aren't letters and would
      // otherwise fall through to a no-op.
      if (e.key === "," && onFrameStep) {
        e.preventDefault();
        onFrameStep(-1, e.shiftKey);
        return;
      }
      if (e.key === "." && onFrameStep) {
        e.preventDefault();
        onFrameStep(1, e.shiftKey);
        return;
      }
      // Clip / note flow — C / M / I / O. These letters are NOT in
      // `TOOL_BINDINGS`, so binding them here can't shadow a tool.
      if ((e.key === "c" || e.key === "C") && onEnterClip) {
        e.preventDefault();
        onEnterClip();
        return;
      }
      if ((e.key === "m" || e.key === "M") && onMarkMoment) {
        e.preventDefault();
        onMarkMoment();
        return;
      }
      if ((e.key === "i" || e.key === "I") && onSetClipIn) {
        e.preventDefault();
        onSetClipIn();
        return;
      }
      if ((e.key === "o" || e.key === "O") && onSetClipOut) {
        e.preventDefault();
        onSetClipOut();
        return;
      }
      if (e.key === "[" && onGoToClipIn) {
        e.preventDefault();
        onGoToClipIn();
        return;
      }
      // Loop / focus — L / F (rebound off the line / freehand tools).
      if ((e.key === "l" || e.key === "L") && onToggleLoop) {
        e.preventDefault();
        onToggleLoop();
        return;
      }
      if ((e.key === "f" || e.key === "F") && onToggleFocus) {
        e.preventDefault();
        onToggleFocus();
        return;
      }
      // Source switch — V / B (rebound off the select / ball tools).
      if ((e.key === "v" || e.key === "V") && onSwitchSource) {
        e.preventDefault();
        onSwitchSource("video");
        return;
      }
      if ((e.key === "b" || e.key === "B") && onSwitchSource) {
        e.preventDefault();
        onSwitchSource("tactical_board");
        return;
      }
      // Quick-pick tool — 1 / 2 / 3 / 4 arm the active surface's
      // first four tools. Handled in-hook (it owns `armTool` + the
      // surface) so the parent doesn't need the editor store.
      if (e.key === "1" || e.key === "2" || e.key === "3" || e.key === "4") {
        const tool = QUICK_TOOLS[surface]?.[Number(e.key) - 1];
        if (tool && isToolAllowed(tool, surface)) {
          e.preventDefault();
          armTool(tool);
        }
        return;
      }

      // Tool shortcuts. Skip if a non-tool key fired.
      const key = e.key.toUpperCase();
      const tool = TOOL_BINDINGS[key];
      if (!tool) return;

      // isToolAllowed gates the tool against the current surface;
      // armTool's own validity check also falls back to select on
      // invalid combinations. We check here too so a key bound to
      // an unavailable tool simply does nothing (visible to the
      // user as "no-op" — they can still type letters elsewhere).
      if (!isToolAllowed(tool, surface)) return;

      e.preventDefault();
      armTool(tool);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    enabled,
    armTool,
    disarm,
    surface,
    onToggleHelp,
    onEnterClip,
    onMarkMoment,
    onSetClipIn,
    onSetClipOut,
    onGoToClipIn,
    onFrameStep,
    onToggleLoop,
    onToggleFocus,
    onSwitchSource,
  ]);
}
